import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import useFormValidatorText from '../../hooks/validators/use-form-validator-text';
import ScreenService from '../../services/use_case/screen/ScreenService';
import ApiResponse from '../../services/ApiResponse';
import { ScreenInputs } from '../Screen/Screen';
import Screen from '../../models/Screen';
import Schedule from '../../models/Schedule';
import License from '../../models/License';
import LicenseService from '../../services/use_case/license/LicenseService';

import {
    CactusLabelInput,
    DashboardTitle,
    DashboardWrapper,
    ScreenPairCode,
    ModalDeleteConfirm,
    CreatableTagsSelect,
    DetailBreadcrumb,
} from '../../components';

import { getOption } from '../../models/Tag';
import Option from '../../models/Option';

import TagsService from '../../services/use_case/tags/TagsService';

interface ScreenDetailProps {};

const ScreenDetail: React.FC<ScreenDetailProps> = (props: ScreenDetailProps) => {

    const [t, i18n] = useTranslation();

    const _screenService: ScreenService = new ScreenService()
    const _tagsService: TagsService = new TagsService()

    let { id: screenId } = useParams<{id:string}>()
    const [isLoading, setisLoading] = useState(false)
    const [isUpdatingForm, setisUpdatingForm] = useState(false)
    const [deletingScreen, setdeletingScreen] = useState(false)
    const [isCreatingTag, setisCreatingTag] = useState(false)
    const [screen, setscreen] = useState<Screen>()
    const [title, settitle] = useState<string>(t('Screen'))

    const [selectedTags, setSelectedTags] = useState<Option[]>([]);
    const [schedules, setSchedules] = useState<Schedule[]>([])
    const [isLoadingSchedules, setIsLoadingSchedules] = useState(false)
    const [license, setLicense] = useState<License | null>(null)

    let history = useHistory()

    const { handleSubmit, register, errors, reset } = useForm<ScreenInputs>({
        defaultValues: {
            ...screen
        }
    });
    const textValidator = register(useFormValidatorText());

    useEffect(() => {
        _getScreen(screenId)
        return () => {
            _screenService.cancelRequest()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const _getScreen = async( id: string ) => {
        setisLoading(true)
        try {
            const screenResponse: AxiosResponse<ApiResponse<Screen>> = await _screenService.show(id);
            const screen: Screen = screenResponse.data.data!
            setisLoading(false)
            setscreen(screen)
            setSelectedTags(screen.tags.map(tag => getOption(tag)))
            reset(screen)
            settitle(t('Screen') + ": " + screen.name);
            _getSchedules(id)
            _getLicense(id)
        } catch (error) {
            history.replace('/screens')
        }
    }

    const _getSchedules = async(id: string) => {
        setIsLoadingSchedules(true)
        try {
            const response = await _screenService.schedulesByScreenId(id)
            setSchedules(response.data?.data?.data ?? [])
            setIsLoadingSchedules(false)
        } catch (error) {
            setIsLoadingSchedules(false)
        }
    }

    const _getLicense = async(id: string) => {
        try {
            const response = await new LicenseService().fetch(1)
            const licenses: License[] = response.data?.data?.data ?? []
            const found = licenses.find(l => (l.resources as Screen)?.id === id) ?? null
            setLicense(found)
        } catch (error) {}
    }

    const _deleteScreen = async() => {
        setisUpdatingForm(true)
        try {
            await _screenService.delete(screenId)
            setisUpdatingForm(false)
            setdeletingScreen(false)
            history.replace('/screens')
        } catch (error) {
            setisUpdatingForm(false)
        }
    }

    const _handleOnSubmit = async(data: FormData) => {
        setisUpdatingForm(true)
        try {
            const updateResponse: AxiosResponse<ApiResponse<Screen>> = await _screenService.update(screenId, data)
            setisUpdatingForm(false)
            setscreen(updateResponse.data.data!)
            settitle(updateResponse.data.data!.name)
        } catch(error) {
            setisUpdatingForm(false)
        }
    }

    const _handleCreateTag = async (name: string) => {
        setisCreatingTag(true)
        try {
            const formData = new FormData()
            formData.append('name', name)
            const response = await _tagsService.store(formData)
            const newTag = response.data.data!
            setSelectedTags(prev => [...prev, getOption(newTag)])
            setisCreatingTag(false)
        } catch(error) {
            setisCreatingTag(false)
        }
    }

    const _handleSyncTags = async(json: any) => {
        setisUpdatingForm(true)
        try {
            const selectedTagsIds = selectedTags.map(tag => tag.value);
            json["tag_ids"] = selectedTagsIds;
            await _screenService.syncTags(screenId, json)
            setisUpdatingForm(false)
        } catch(error) {
            setisUpdatingForm(false)
        }
    }

    const _formatDate = (dateStr: string): string => {
        try {
            return new Date(dateStr).toLocaleDateString(i18n.language, {
                year: 'numeric', month: 'short', day: 'numeric'
            });
        } catch { return dateStr; }
    }

    const _getStatusVariant = (): 'configured' | 'unpaired' => {
        if (!screen || !screen.paircode_linked) return 'unpaired';
        return 'configured';
    }

    const _renderStatusBadge = () => {
        if (!screen) return null;
        const variant = _getStatusVariant();
        const labels = { configured: t('Configured'), unpaired: t('Unpaired') };
        return (
            <div className={`screen-item__status-badge screen-item__status-badge--${variant}`}>
                <span className="screen-item__status-dot" />
                <span className="screen-item__status-label">{ labels[variant] }</span>
            </div>
        );
    }

    const _renderScheduleItem = (schedule: Schedule) => {
        const filledBars = schedule.priority + 1;
        const barColorClass = [
            'screen-detail__schedule-bar--p0',
            'screen-detail__schedule-bar--p1',
            'screen-detail__schedule-bar--p2',
        ][schedule.priority] ?? 'screen-detail__schedule-bar--p0';

        const dateText = schedule.is_always_schedule
            ? `${_formatDate(schedule.start_date)} →`
            : `${_formatDate(schedule.start_date)} → ${_formatDate(schedule.end_date)}`;

        const days = schedule.show_days ?? [];
        const dayAbbrs = days.map(d =>
            t(d.charAt(0).toUpperCase() + d.slice(1)).slice(0, 3)
        );

        return (
            <div key={schedule.id} className="screen-detail__schedule-item">
                <div className="screen-detail__schedule-priority">
                    {[0, 1, 2].map(i => (
                        <div
                            key={i}
                            className={`screen-detail__schedule-bar${i < filledBars ? ` ${barColorClass}` : ''}`}
                        />
                    ))}
                </div>
                <div className="screen-detail__schedule-name">{schedule.name}</div>
                <div className="screen-detail__schedule-meta">
                    <span className="screen-detail__schedule-meta-item">{dateText}</span>
                    {schedule.is_time_slot && (
                        <span className="screen-detail__schedule-meta-item">
                            {schedule.start_time} – {schedule.end_time}
                        </span>
                    )}
                    {dayAbbrs.length > 0 && (
                        <div className="screen-detail__schedule-days">
                            {dayAbbrs.map((day, i) => (
                                <span key={i} className="screen-detail__schedule-day">{day}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const _renderSchedulesSection = () => {
        return (
            <div className="row mt-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <p className="screen-detail__card-title">{t('Schedule')}</p>
                            {isLoadingSchedules ? (
                                <p className="screen-detail__schedule-empty">{t('Loading...')}</p>
                            ) : schedules.length === 0 ? (
                                <p className="screen-detail__schedule-empty">{t('No Result')}</p>
                            ) : (
                                schedules.map(s => _renderScheduleItem(s))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const _renderPairCode = () => {
        return (
            <div className="col-md-6 mx-auto">
                <div className="card">
                    <div className="card-body py-5">
                        <ScreenPairCode paircode={screen?.pair_code} />
                    </div>
                </div>
            </div>
        )
    }

    const _renderScreenDetailBody = () => {
        return screen?.paircode_linked ? _renderScreenForm() : _renderPairCode()
    }

    const _renderScreenForm = () => {
        return (
            <>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <div className="card h-100">
                            <div className="card-body">
                                <p className="screen-detail__card-title">{t('Details')}</p>
                                <form onSubmit={handleSubmit(_handleOnSubmit)}>
                                    <CactusLabelInput
                                        labelTitle={t('Name')}
                                        inputAttributes={{
                                            id: "name",
                                            name: "name",
                                            type: "text",
                                            placeholder: t('Name'),
                                            autoComplete: "name",
                                            ref: textValidator,
                                        }}
                                        errorMessage={errors.name ? errors.name.message : null}
                                    />
                                    {screen?.hardware_key && (
                                        <div className="screen-detail__info-row mt-3">
                                            <span className="screen-detail__info-label">{t('Hardware_key')}</span>
                                            <span className="screen-detail__info-value">{screen.hardware_key}</span>
                                        </div>
                                    )}
                                    {license && (
                                        <div className="screen-detail__info-row mt-2">
                                            <span className="screen-detail__info-label">{t('License')}</span>
                                            <Link
                                                to={`/licenses/${license.id}`}
                                                className="screen-detail__license-link"
                                            >
                                                {license.id}
                                            </Link>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-end mt-3">
                                        <button
                                            type="submit"
                                            className={`btn btn-cactus${isUpdatingForm ? ' btn-cactus--loading' : ''}`}
                                            disabled={isUpdatingForm}
                                        >
                                            {t('Update_screen')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <p className="screen-detail__card-title">{t('Tags')}</p>
                                <form className="d-flex flex-column flex-grow-1" onSubmit={handleSubmit(_handleSyncTags)}>
                                    <CreatableTagsSelect
                                        value={selectedTags}
                                        onChange={(selectedOption: Option[]) => {
                                            setSelectedTags(selectedOption);
                                        }}
                                        cache={isCreatingTag}
                                        closeMenuOnSelect={false}
                                        onCreateOption={_handleCreateTag}
                                        isCreating={isCreatingTag}
                                    />
                                    <div className="d-flex justify-content-end mt-auto pt-3">
                                        <button
                                            type="submit"
                                            className={`btn btn-cactus${isUpdatingForm ? ' btn-cactus--loading' : ''}`}
                                            disabled={isUpdatingForm}
                                        >
                                            {t('Update_tags')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

            </>
        )
    }

    return(
        <DashboardWrapper>
            <div className="row mb-4">
                <div className="col">
                    <DetailBreadcrumb items={[
                        { label: t('Screens'), to: '/screens' },
                        { label: title },
                    ]} />
                    <DashboardTitle title={title} isLoading={isLoading} />
                    {screen && !isLoading && _renderStatusBadge()}
                </div>
                {screen && !isLoading && (
                    <div className="col-auto d-flex align-items-end pb-2">
                        <button
                            type="button"
                            onClick={() => setdeletingScreen(true)}
                            className="btn btn-cactus--red"
                            disabled={isUpdatingForm}
                        >
                            {t('Delete_screen')}
                        </button>
                    </div>
                )}
            </div>

            {!isLoading && _renderScreenDetailBody()}
            {screen && !isLoading && !!screen.paircode_linked && _renderSchedulesSection()}

            <ModalDeleteConfirm
                show={deletingScreen}
                title={t('Delete_screen')}
                onCloseFunction={setdeletingScreen}
                onCloseFunctionParameter={false}
                entityTranslation={t('screen')}
                buttonLoading={isUpdatingForm}
                onClickFunctionButton={_deleteScreen}
                buttonText={t('Delete')}
            />
        </DashboardWrapper>
    );
};

export default React.memo(ScreenDetail);
